import { Phone, Camera, FileCheck, Wrench } from "lucide-react";
import { Seo, breadcrumbSchema, faqSchema } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TrustStrip } from "../components/TrustStrip";
import { EstimateForm } from "../components/EstimateForm";
import { Reveal, Eyebrow } from "../components/Reveal";
import { BIZ, track, cleanText } from "../data/site";

const FAQS = [
  { q: "Is the estimate really free?", a: "Yes — completely free with zero obligation, whether you submit photos online or bring the vehicle to our Carrollton facility for an in-person evaluation." },
  { q: "How accurate is a photo estimate?", a: "Photo estimates give a reliable range for visible damage. A teardown-based blueprint at our shop confirms the exact figure before any work begins — hidden damage is documented and handled through your insurer." },
  { q: "How fast will I hear back?", a: "A master technician reviews every submission and responds within one business hour during shop hours (Mon–Fri 7:30 AM–6:00 PM, Sat 8:00 AM–5:00 PM)." },
  { q: "What photos should I take?", a: "Take wide shots of the whole vehicle, mid-range shots of each damaged panel, and close-ups of the worst damage — plus the VIN plate if easy to reach. Up to 6 photos per submission." },
];

const NEXT_STEPS = [
  { icon: Camera, t: "You send photos", d: "Upload damage photos and vehicle details through the form." },
  { icon: FileCheck, t: "We assess & respond", d: "A master technician reviews everything and replies within one business hour." },
  { icon: Wrench, t: "You decide", d: "Get your written estimate, then book whenever you are ready — no pressure." },
];

export default function FreeEstimate() {
  return (
    <div data-testid="free-estimate-page">
      <Seo
        title="Free Collision Repair Estimates in Carrollton, TX | Westway"
        description="Free photo estimates in Carrollton, TX — upload damage photos, get a master technician's review within one business hour. Or call (214) 888-2886."
        path="/free-estimate"
        schemas={[
          faqSchema(FAQS),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Free Estimate", path: "/free-estimate" }]),
        ]}
      />

      <section className="relative pt-32 lg:pt-40 pb-14 overflow-hidden">
        <div className="absolute inset-0 surface-weave opacity-50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Free Estimate", path: "/free-estimate" }]} />
          <Reveal>
            <Eyebrow>Zero Obligation</Eyebrow>
            <h1 className="mt-5 font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.06] text-balance max-w-4xl">
              Free Collision Repair Estimates in Carrollton, TX
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
              A free estimate from Westway Collision Center is a written, itemized repair plan
              prepared by a master technician — not a guess. Upload photos below for a fast review,
              or call <a href={BIZ.phoneHref} onClick={() => track("click_to_call", { location: "estimate_hero" })} className="text-accent hover:text-accentdeep transition-colors">{BIZ.phoneDisplay}</a> for
              an immediate conversation. Estimates are always free, always without obligation.
            </p>
          </Reveal>
        </div>
      </section>

      <TrustStrip />

      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="rounded-2xl border border-white/[0.08] bg-surface p-7 sm:p-10">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">Start Your Free Estimate</h2>
              <p className="mt-3 text-sm sm:text-base text-slate-400">
                The more detail and photos you share, the sharper the estimate. All fields marked * are required.
              </p>
              <div className="mt-8">
                <EstimateForm testid="free-estimate" />
              </div>
            </div>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {NEXT_STEPS.map((n, i) => (
              <Reveal key={n.t} delay={i * 0.07}>
                <div className="h-full rounded-2xl border border-white/[0.08] bg-surface/60 p-6">
                  <n.icon size={24} className="text-accent" strokeWidth={1.6} aria-hidden="true" />
                  <h3 className="mt-3 font-display text-base font-semibold text-slate-100">{n.t}</h3>
                  <p className="mt-2 text-xs sm:text-sm text-slate-400 leading-relaxed">{n.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-12">
            <div className="rounded-2xl border border-accent/25 bg-gradient-to-br from-accent/[0.08] via-surface to-surface p-8 text-center">
              <h2 className="font-display text-xl sm:text-2xl font-bold text-white">Rather Talk It Through?</h2>
              <p className="mt-2 text-sm text-slate-400">Urgent, towing, or complex damage — call and get answers immediately.</p>
              <a
                href={BIZ.phoneHref}
                data-testid="estimate-call-cta"
                onClick={() => track("click_to_call", { location: "estimate_page" })}
                className="mt-5 inline-flex items-center gap-2.5 rounded-full bg-accentdeep px-8 py-4 font-display font-bold text-white transition-all duration-300 hover:bg-accenthover hover:-translate-y-0.5"
              >
                <Phone size={17} strokeWidth={2.2} />
                Call {BIZ.phoneDisplay}
              </a>
            </div>
          </Reveal>

          <div className="mt-16">
            <Reveal className="mb-8">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">Free Estimate FAQs</h2>
            </Reveal>
            <div className="flex flex-col gap-4">
              {FAQS.map((f) => (
                <Reveal key={f.q}>
                  <div className="rounded-2xl border border-white/[0.08] bg-surface/50 p-6 sm:p-7">
                    <h3 className="font-display text-base sm:text-lg font-semibold text-slate-100">{f.q}</h3>
                    <p className="mt-2.5 text-sm sm:text-base text-slate-400 leading-relaxed">{cleanText(f.a)}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
